import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationAction {
  label: string;
  callback: () => void;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  action?: NotificationAction;
  autoDismiss?: boolean;
  dismissAfter?: number; // milliseconds
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications = signal<Notification[]>([]);
  public notifications$ = this.notifications.asReadonly();

  private nextId = 0;

  /**
   * Show a success notification
   */
  success(title: string, message: string, autoDismiss = true, dismissAfter = 5000): string {
    return this.show({ type: 'success', title, message, autoDismiss, dismissAfter });
  }

  /**
   * Show an error notification
   */
  error(title: string, message: string, action?: NotificationAction, autoDismiss = false): string {
    return this.show({ type: 'error', title, message, action, autoDismiss });
  }

  /**
   * Show a warning notification
   */
  warning(title: string, message: string, action?: NotificationAction, autoDismiss = true, dismissAfter = 10000): string {
    return this.show({ type: 'warning', title, message, action, autoDismiss, dismissAfter });
  }

  /**
   * Show an info notification
   */
  info(title: string, message: string, autoDismiss = true, dismissAfter = 5000): string {
    return this.show({ type: 'info', title, message, autoDismiss, dismissAfter });
  }

  /**
   * Show a generic notification
   */
  private show(options: Omit<Notification, 'id'>): string {
    const id = `notification-${this.nextId++}`;
    const notification: Notification = { id, ...options };

    this.notifications.update(notifs => [...notifs, notification]);

    if (notification.autoDismiss && notification.dismissAfter) {
      setTimeout(() => this.dismiss(id), notification.dismissAfter);
    }

    return id;
  }

  /**
   * Dismiss a notification by ID
   */
  dismiss(id: string): void {
    this.notifications.update(notifs => notifs.filter(n => n.id !== id));
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notifications.set([]);
  }
}
